import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import ProductCard from "./ProductCard";
import { useCatalog } from "../hooks/useCatalog";
import { PRODUCTS as P_TID } from "../constants/testIds";

const MAX_ITEMS = 10;

/**
 * Vitrine « Top ventes » de la home : 10 produits maximum.
 * Sélection : les produits marqués top_selling (badge « Top vente » existant),
 * complétés par les plus grosses promos (original_price - price_from) si le
 * flag en contient moins de 10. Ordre stable et déterministe (ordre du
 * catalogue pour les tops, remise décroissante pour le complément) — choix
 * documenté : pas d'aléatoire ici pour que le HTML prerendu soit cohérent
 * entre les visites. Le catalogue complet reste sur /produits (Products.jsx).
 */
const TopSales = () => {
    const { data: catalogData, isLoading, error } = useCatalog();
    const products = useMemo(() => catalogData?.items || [], [catalogData]);

    const selection = useMemo(() => {
        const numeric = (v) => (v && !Number.isNaN(Number(v)) ? Number(v) : null);
        const tops = products.filter((p) => p.top_selling);
        const pool = [...tops];
        if (pool.length < MAX_ITEMS) {
            const already = new Set(pool.map((p) => p.id));
            const byDiscount = products
                .filter((p) => !already.has(p.id))
                .map((p) => {
                    const price = numeric(p.price_from);
                    const original = numeric(p.original_price);
                    const discount =
                        price !== null && original !== null && original > price
                            ? original - price
                            : 0;
                    return { p, discount };
                })
                .sort((a, b) => b.discount - a.discount);
            pool.push(...byDiscount.map((e) => e.p));
        }
        return pool.slice(0, MAX_ITEMS);
    }, [products]);

    return (
        <section
            id="produits"
            data-testid={P_TID.section}
            className="relative bg-[#FBFBFD] pt-8 md:pt-10 pb-24 md:pb-32"
        >
            <div className="max-w-[1320px] mx-auto px-4 sm:px-6 md:px-10">
                <div className="flex flex-col items-center text-center">
                    <span className="dp-eyebrow">Sélection</span>
                    <h2 className="mt-3 font-display text-3xl md:text-[2.6rem] font-semibold tracking-tight text-[#1C1C1E]">
                        Top ventes
                    </h2>
                    <p className="mt-3 max-w-xl text-[14px] md:text-[15px] leading-relaxed text-neutral-500">
                        Les accessoires préférés de nos clients
                    </p>
                </div>

                {isLoading ? (
                    <div className="mt-16 flex items-center justify-center gap-3 text-neutral-500">
                        <Loader2 className="w-6 h-6 animate-spin text-[#d4af37]" />
                        <span className="text-sm font-medium">Chargement des produits…</span>
                    </div>
                ) : error ? (
                    <div className="mt-10 rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
                        <p className="font-medium">Impossible de charger les produits.</p>
                    </div>
                ) : (
                    <>
                        <div className="mt-10 md:mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 sm:gap-x-6 gap-y-10 sm:gap-y-12">
                            {selection.map((p, i) => (
                                <ProductCard key={p.id} product={p} index={i} />
                            ))}
                        </div>
                        <div className="mt-12 flex justify-center">
                            <Link to="/produits" className="dp-btn dp-btn-primary">
                                Voir tout
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default TopSales;
